// DefaultNavbar.tsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Badge,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Menu as MenuIcon, ChevronLeft, School, Notifications, Logout } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useAuthStore } from "@/store/useAuthStore";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import type { NavbarRoute } from "@/interfaces";
import authService from "@/services/authService";

export interface NotificationPayload {
  id: string;
  title: string;
  message: string;
  timestamp: string;
}

interface DefaultNavbarProps {
  routes: NavbarRoute[];
  onMinimizeToggle: (sidebarOpen: boolean) => void;
}

const DefaultNavbar: React.FC<DefaultNavbarProps> = ({ routes, onMinimizeToggle }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useAppNavigate();
  const profile = useAuthStore((state) => state.user);
  const { enqueueSnackbar } = useSnackbar();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activePath, setActivePath] = useState<string>("my/dashboard");

  // State control for Profile menu
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const isProfileMenuOpen = Boolean(profileAnchorEl);

  // State control for Notifications menu & list
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const isNotifMenuOpen = Boolean(notifAnchorEl);
  const [notifications, setNotifications] = useState<NotificationPayload[]>([]);
  const { avatarUrl } = useAuthStore();

  const primaryMain = theme.palette.primary.main;

  // Listen for globally dispatched notification events
  useEffect(() => {
    const handleAppNotification = (event: Event) => {
      const customEvent = event as CustomEvent<NotificationPayload>;
      const data = customEvent.detail;
      console.log("🔔 [Navbar UI] Displaying Notification:", data);
      // 1. Update state to increment Badge Counter & List Items
      setNotifications((prev) => [data, ...prev]);
      // 2. Display Toast Banner
      enqueueSnackbar(`${data.title}: ${data.message}`, {
        variant: "info",
        autoHideDuration: 4000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    };
    window.addEventListener("app-notification", handleAppNotification);
    return () => {
      window.removeEventListener("app-notification", handleAppNotification);
    };
  }, [enqueueSnackbar]);

  // Structural grouping
  const mockSection = routes.find((r) => r.key === "mock");
  const revisionSection = routes.find((r) => r.key === "revision");
  const mySection = routes.find((r) => r.key === "my");

  const personalItems = mySection?.collapse?.[0]?.collapse || [];

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    onMinimizeToggle(!!sidebarOpen);
  };

  const handleNavClick = (route: string) => {
    if (route) {
      setActivePath(route);
      navigate(`/${route}`);
    }
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotifClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleClearNotifications = () => {
    setNotifications([]);
    handleNotifClose();
  };

  return (
    <>
      {/* --- TOP APP BAR --- */}
      <Box
        component={motion.header}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          height: 64,
          bgcolor: alpha(theme.palette.background.default, 0.96),
          backdropFilter: "blur(20px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
        }}
      >
        {/* Left Section: Brand Only */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }} onClick={() => navigate("/")}>
          <Avatar
            src="/logo.png"
            alt="ComplianceQuizHK"
            sx={{
              width: 44,
              height: 44,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              display: { xs: "none", sm: "block" },
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            {t("navbars.defaultNavbar.brandTitle")}
          </Typography>
        </Box>

        {/* Right Section: Status Actions & Dropdowns */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Notifications Trigger */}
          <Tooltip title="Notifications">
            <IconButton onClick={handleNotifClick} sx={{ fontSize: "1.5rem" }}>
              <Badge badgeContent={notifications.length} color="error">
                <Notifications sx={{ fontSize: "1.5rem" }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notifAnchorEl}
            open={isNotifMenuOpen}
            onClose={handleNotifClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  mt: 1.5,
                  width: 320,
                  maxHeight: 400,
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
                  p: 0.5,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Notifications
              </Typography>
              {notifications.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: "primary.main", cursor: "pointer", fontWeight: 600 }}
                  onClick={handleClearNotifications}
                >
                  Clear All
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 0.5 }} />

            {notifications.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No new notifications
                </Typography>
              </Box>
            ) : (
              notifications.map((item, idx) => (
                <MenuItem
                  key={item.id || idx}
                  onClick={handleNotifClose}
                  sx={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", mt: 0.5 }}>
                    {item.message}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled", mt: 1, fontSize: "0.7rem" }}>
                    {item.timestamp ? new Date(item.timestamp).toLocaleTimeString() : ""}
                  </Typography>
                </MenuItem>
              ))
            )}
          </Menu>

          {/* Profile Avatar & Menu */}
          <>
            <Tooltip title={t("navbars.defaultNavbar.personalSpaceMenu")}>
              <Avatar
                src={avatarUrl || undefined}
                onClick={handleProfileClick}
                sx={{
                  bgcolor: primaryMain,
                  cursor: "pointer",
                  width: 36,
                  height: 36,
                  fontSize: "0.95rem",
                  transition: "transform 0.15s ease",
                  "&:hover": { transform: "scale(1.05)" },
                }}
              >
                {profile?.name?.[0]}
              </Avatar>
            </Tooltip>

            <Menu
              anchorEl={profileAnchorEl}
              open={isProfileMenuOpen}
              onClose={handleProfileClose}
              onClick={handleProfileClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    mt: 1.5,
                    width: 220,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                    p: 0.5,
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography noWrap variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {profile?.name || ""}
                </Typography>
                <Typography noWrap variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  {t("navbars.defaultNavbar.personalAccount")}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />

              {personalItems
                .filter((item: NavbarRoute) => !item.isHide)
                .map((item: NavbarRoute) => {
                  const isActive = activePath === item.route;
                  return (
                    <MenuItem
                      key={item.key}
                      onClick={() => item.route && handleNavClick(item.route)}
                      selected={isActive}
                      sx={{
                        borderRadius: 1,
                        my: 0.25,
                        py: 1,
                        color: isActive ? "primary.main" : "text.primary",
                        "&.Mui-selected": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          fontWeight: 600,
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: isActive ? "primary.main" : "text.secondary", minWidth: 36 }}>
                        {item.icon || <School sx={{ fontSize: "1.25rem" }} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: "0.875rem",
                              fontWeight: isActive ? 600 : 500,
                            },
                          },
                        }}
                      />
                    </MenuItem>
                  );
                })}

              {/* Logout Button */}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  authService.logout();
                  navigate("/login");
                }}
                sx={{
                  borderRadius: 1,
                  my: 0.25,
                  py: 1,
                  color: "error.main",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ color: "error.main", minWidth: 36 }}>
                  <Logout sx={{ fontSize: "1.25rem" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("navbars.defaultNavbar.logout")}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      },
                    },
                  }}
                />
              </MenuItem>
            </Menu>
          </>
        </Box>
      </Box>

      {/* --- LEFT SIDEBAR --- */}
      <Box
        component="nav"
        sx={{
          position: "fixed",
          left: 0,
          top: 64,
          bottom: 0,
          width: sidebarOpen ? 280 : 72,
          bgcolor: alpha(theme.palette.background.paper, 0.98),
          borderRight: `1px solid ${theme.palette.divider}`,
          transition: "width 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
          zIndex: 1200,
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarOpen ? "space-between" : "center",
            px: sidebarOpen ? 2.5 : 1,
            py: 1.5,
            minHeight: 64,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            transition: "padding 0.2s ease-in-out",
          }}
        >
          <Typography
            noWrap
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "text.primary",
              opacity: sidebarOpen ? 1 : 0,
              width: sidebarOpen ? "auto" : 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              transition: "opacity 0.15s ease-in-out, width 0.15s ease-in-out",
              userSelect: "none",
              background: "linear-gradient(135deg, #FF416C 0%, #FF4B2B 50%, #FF8C00 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: theme.palette.mode === "dark" ? "drop-shadow(0px 2px 4px rgba(255, 75, 43, 0.15))" : "none",
            }}
          >
            {t("navbars.defaultNavbar.sidebarTagline")}
          </Typography>
          <IconButton
            onClick={handleToggleSidebar}
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              borderRadius: 2,
              color: "text.secondary",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: "primary.main",
                transform: "scale(1.05)",
              },
              width: 38,
              height: 38,
            }}
          >
            {sidebarOpen ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ p: 1.5, pt: 2, flex: 1, overflowY: "auto" }}>
          {/* Mock Exams Section */}
          <Typography
            variant="caption"
            sx={{
              pl: 2,
              mb: 1,
              color: "text.secondary",
              fontWeight: 700,
              display: sidebarOpen ? "block" : "none",
              letterSpacing: "0.05em",
            }}
          >
            {mockSection?.name}
          </Typography>

          {mockSection && (
            <MenuGroup section={mockSection} sidebarOpen={sidebarOpen} activePath={activePath} onNavigate={handleNavClick} />
          )}

          <Divider sx={{ my: 2, mx: 1 }} />

          {/* Revision Section */}
          <Typography
            variant="caption"
            sx={{
              pl: 2,
              mb: 1,
              color: "text.secondary",
              fontWeight: 700,
              display: sidebarOpen ? "block" : "none",
              letterSpacing: "0.05em",
            }}
          >
            {revisionSection?.name}
          </Typography>

          {revisionSection && (
            <MenuGroup
              section={revisionSection}
              sidebarOpen={sidebarOpen}
              activePath={activePath}
              onNavigate={handleNavClick}
            />
          )}
        </Box>
      </Box>

      {/* Content Spacer */}
      <Box sx={{ width: sidebarOpen ? 280 : 72, transition: "width 0.2s", flexShrink: 0 }} />
    </>
  );
};

/* --- INNER MENUGROUP WORKSPACE --- */
interface MenuGroupProps {
  section: NavbarRoute;
  sidebarOpen: boolean;
  activePath: string;
  onNavigate: (route: string) => void;
}

const MenuGroup = ({ section, sidebarOpen, activePath, onNavigate }: MenuGroupProps) => {
  const theme = useTheme();
  const subItems = section.collapse?.[0]?.collapse || [];

  return (
    <>
      {subItems
        .filter((item: NavbarRoute) => !item.isHide)
        .map((item: NavbarRoute) => {
          const isActive = activePath === item.route;

          return (
            <Box
              key={item.key}
              onClick={() => item.route && onNavigate(item.route)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: sidebarOpen ? "flex-start" : "center",
                gap: sidebarOpen ? 2 : 0,
                px: sidebarOpen ? 2 : 0,
                py: 1.25,
                mx: 0.75,
                my: 0.5,
                borderRadius: 1,
                cursor: "pointer",
                transition: "all 0.15s ease-in-out",
                bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                color: isActive ? theme.palette.primary.main : "text.secondary",
                "&:hover": {
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.text.primary, 0.04),
                  color: "text.primary",
                },
                width: sidebarOpen ? "auto" : 44,
                height: sidebarOpen ? "auto" : 44,
              }}
            >
              <Box
                sx={{
                  fontSize: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isActive ? "primary.main" : "inherit",
                }}
              >
                {item.icon || <School sx={{ fontSize: "1.25rem" }} />}
              </Box>

              {sidebarOpen && (
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.name}
                </Typography>
              )}
            </Box>
          );
        })}
    </>
  );
};

export default DefaultNavbar;
